import React, {ChangeEvent, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ThumbnailLayout} from './Thumbnails/MediaGalleryThumbnailFactory';
import {
  Vertical,
  Horizontal,
  Button,
  MoreIcon,
  DialogHeader,
  RadioButton,
  Checkbox,
  TrashIcon,
  ThemeName,
  ButtonWithPopout,
  List,
  ListItem,
} from '@taopix/taopix-design-system';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import {PortalProps} from '@taopix/taopix-design-system/dist/types/Components/Portal/Portal';
import {buttonSizeTypes} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';

export type MediaGalleryOptionsPopoutProps = {
  onSort: Function;
  onChangeSortDirection: Function;
  onChangeThumbnailView: Function;
  buttonId: string;
  selectedPictureAction: Function;
  onHideUsed?: Function;
  showSortByProject?: boolean;
  showRemoveImages?: boolean;
  defaultSortType: PictureSortType;
  defaultSortDirection: PictureSortDirection;
  defaultThumbnailStyle: ThumbnailLayout;
  buttonSize?: buttonSizeTypes;
  closeOnChange?: boolean;
} & Partial<
  Pick<
    PopOutProps,
    'transformOrigin' | 'anchorOrigin' | 'displayMode' | 'edgeBehaviour' | 'theme' | 'themeTip'
  >
> &
  Partial<Pick<PortalProps, 'repositionOnResize'>>;

export enum PictureSortType {
  DateTaken = 'datetimeoriginal',
  Filename = 'name',
  Size = 'fileSizeBytes',
  Orientation = 'orientation',
  Project = 'project',
}

export type PictureSortDirection = 'asc' | 'desc';

export enum PictureAction {
  RotateSelected,
  RemoveSelected,
  RemoveAll,
}

export enum ThumbnailViewMode {
  Detailed = 'details-list',
  Full = 'full-frame-tiles',
  Square = 'square-tiles',
}

const MediaGalleryOptionsPopout = ({
  onSort,
  onChangeSortDirection,
  onChangeThumbnailView,
  buttonId,
  selectedPictureAction,
  anchorOrigin = {vertical: Vertical.Bottom, horizontal: Horizontal.Left},
  transformOrigin = {vertical: Vertical.Top, horizontal: Horizontal.Left},
  displayMode,
  onHideUsed,
  showSortByProject = false,
  showRemoveImages = true,
  defaultSortType,
  defaultSortDirection,
  defaultThumbnailStyle,
  buttonSize = 'small',
  closeOnChange,
  ...props
}: MediaGalleryOptionsPopoutProps) => {
  const [mediaOptionsMenuOpen, setMediaOptionsMenuOpen] = useState<boolean>(false);
  const [selectedPictureSortType, setSelectedPictureSortType] =
    useState<PictureSortType>(defaultSortType);
  const [selectedPictureSortDirection, setSelectedPictureSortDirection] =
    useState<PictureSortDirection>(defaultSortDirection);
  const [selectedThumbnailStyle, setSelectedThumbnailStyle] =
    useState<ThumbnailLayout>(defaultThumbnailStyle);
  const [hideUsedChecked, setHideUsedChecked] = useState<boolean>(false);
  const menuButtonRef = useRef<HTMLDivElement | null>(null);

  const toggleMediaOptionsMenu = () => {
    setMediaOptionsMenuOpen(!mediaOptionsMenuOpen);
  };

  const onRadioChange = (sortType: PictureSortType) => {
    setSelectedPictureSortType(sortType);
    onSort(sortType);
  };

  const onReverseSortOrder = (isReversed: boolean): void => {
    setSelectedPictureSortDirection(isReversed ? 'desc' : 'asc');
    onChangeSortDirection(isReversed);
  };

  const onChangeViewMode = (isChecked: boolean, event: ChangeEvent<HTMLInputElement>) => {
    setSelectedThumbnailStyle(current => {
      return ThumbnailViewMode.Detailed === current
        ? ThumbnailViewMode.Full
        : ThumbnailViewMode.Detailed;
    });
  };

  const onChangeHideUsed = () => {
    if (onHideUsed) {
      onHideUsed();
      setHideUsedChecked(!hideUsedChecked);
    }
  };

  useEffect(() => {
    onChangeThumbnailView(selectedThumbnailStyle);
    onSort(selectedPictureSortType);
    if (closeOnChange) {
      setMediaOptionsMenuOpen(false);
    }
  }, [selectedPictureSortType, selectedThumbnailStyle]);

  const {t} = useTranslation(['*', 'picturemanager']);

  return (
    <div
      id="tds-media-options-menu"
      className="tds-media-options-menu"
      role="presentation"
      ref={menuButtonRef}
    >
      <ButtonWithPopout
        type="menu"
        returnFocusToTriggerOnItemClick
        buttonProps={{
          id: buttonId,
          buttonStyle: 'tertiary',
          label: t('str_LabelPictureListOptions', {
            ns: 'picturemanager',
          }),
          startIcon: <MoreIcon />,
          hideLabel: true,
          size: buttonSize,
        }}
        popoutProps={{
          id: 'tds-media-options-menu-popout',
          anchorId: buttonId,
          anchorOrigin: {
            vertical: anchorOrigin.vertical,
            horizontal: anchorOrigin.horizontal,
          },
          transformOrigin: {
            vertical: transformOrigin.vertical,
            horizontal: transformOrigin.horizontal,
          },
          popDirection: 'down',
          displayMode: displayMode,
          shadowRoot: document.querySelector('#root')?.shadowRoot,
          className: 'flex flex-col sm:w-[min(300rem,theme(width.bounds))]',
          edgeBehaviour: props.edgeBehaviour,
          theme: props.theme,
          themeTip: props.themeTip,
          contentTheme: ThemeName.Popouts,
        }}
      >
        <div className={'lg:py-sm sm:w-[300rem] sm:max-w-full'}>
          {displayMode === 'modal' && <DialogHeader>{t('str_TitleOptions')}</DialogHeader>}
          <List className={'overflow-y-auto'} size={'medium'}>
            <ListItem size={'medium'}>
              <RadioButton
                onChange={() => onRadioChange(PictureSortType.DateTaken)}
                label={t('str_LabelSortByDateTaken', {
                  ns: 'picturemanager',
                })}
                groupName={'sort'}
                id={'0'}
                defaultChecked={PictureSortType.DateTaken === selectedPictureSortType}
              />
            </ListItem>

            <ListItem size={'medium'}>
              <RadioButton
                onChange={() => onRadioChange(PictureSortType.Orientation)}
                label={t('str_LabelSortByOrientation', {
                  ns: 'picturemanager',
                })}
                groupName={'sort'}
                id={'1'}
                defaultChecked={PictureSortType.Orientation === selectedPictureSortType}
              />
            </ListItem>

            <ListItem size={'medium'}>
              <RadioButton
                onChange={() => onRadioChange(PictureSortType.Size)}
                label={t('str_LabelSortBySize', {
                  ns: 'picturemanager',
                })}
                groupName={'sort'}
                id={'2'}
                defaultChecked={PictureSortType.Size === selectedPictureSortType}
              />
            </ListItem>

            <ListItem size={'medium'}>
              <RadioButton
                onChange={() => onRadioChange(PictureSortType.Filename)}
                label={t('str_LabelSortByFilename', {
                  ns: 'picturemanager',
                })}
                groupName={'sort'}
                id={'3'}
                defaultChecked={PictureSortType.Filename === selectedPictureSortType}
              />
            </ListItem>

            {showSortByProject && (
              <ListItem size={'medium'}>
                <RadioButton
                  onChange={() => onRadioChange(PictureSortType.Project)}
                  label={t('str_LabelSortByProject', {
                    ns: 'picturemanager',
                  })}
                  groupName={'sort'}
                  id={'4'}
                  defaultChecked={PictureSortType.Project === selectedPictureSortType}
                />
              </ListItem>
            )}
            <hr className={'my-sm mx-md border-themeTextColour opacity-50'} />

            {onHideUsed && (
              <ListItem size={'medium'}>
                <Checkbox
                  label={t('*:str_LabelHideUsedPictures')}
                  id="hide-used-pics"
                  onChange={onChangeHideUsed}
                  defaultChecked={hideUsedChecked}
                />
              </ListItem>
            )}

            <ListItem size={'medium'}>
              <Checkbox
                onChange={onReverseSortOrder}
                label={t('picturemanager:str_LabelReverseSortOrder')}
                id={'6'}
                defaultChecked={'desc' === selectedPictureSortDirection}
                disabled={false}
              />
            </ListItem>

            <ListItem size={'medium'}>
              <Checkbox
                onChange={onChangeViewMode}
                label={t('str_LabelShowFileInfo', {
                  ns: 'picturemanager',
                })}
                id={'5'}
                defaultChecked={ThumbnailViewMode.Detailed === selectedThumbnailStyle}
              />
            </ListItem>

            {showRemoveImages && (
              <ListItem>
                <Button
                  label={t('str_LabelRemoveAllPictures', {
                    ns: 'picturemanager',
                  })}
                  buttonStyle={'standard'}
                  corners="square"
                  labelAlignment="left"
                  startIcon={<TrashIcon />}
                  onClick={() => selectedPictureAction(PictureAction.RemoveAll)}
                  value={PictureAction.RemoveAll}
                  className={'px-md'}
                />
              </ListItem>
            )}
          </List>
        </div>

        {displayMode === 'modal' && (
          <Button
            label={t('str_ButtonClose')}
            buttonStyle="negative"
            corners={'square'}
            onClick={toggleMediaOptionsMenu}
          />
        )}
      </ButtonWithPopout>
    </div>
  );
};

export default MediaGalleryOptionsPopout;
